// Settings → Subscription: self-service plan management for self-serve
// subscribers. "Manage subscription" opens Stripe's secure customer portal,
// where the owner can cancel (access continues to the end of the paid month),
// update the card on file and download invoices. Hidden for companies that
// don't pay through a self-serve subscription.
import { CreditCard } from "lucide-react";
import { useEffect, useState } from "react";
import { billingSubscription, type BillingSubscription } from "../lib/api";

function day(iso: string | null) {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
}

const STATUS: Record<string, string> = {
  active: "Active", trialing: "Trial", past_due: "Payment failed — update your card", unpaid: "Unpaid — update your card",
  canceled: "Canceled", incomplete: "Payment incomplete", incomplete_expired: "Payment expired",
};

export default function SubscriptionCard() {
  const [sub, setSub] = useState<BillingSubscription | null>(null);
  const [loadErr, setLoadErr] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [opening, setOpening] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const billingParam = typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("billing") : null;
  const returned = billingParam === "returned";
  const renewed = billingParam === "renewed";

  useEffect(() => {
    let cancelled = false;
    setLoadErr(false);
    billingSubscription.get()
      .then((s) => { if (!cancelled) setSub(s); })
      .catch(() => { if (!cancelled) setLoadErr(true); });
    return () => { cancelled = true; };
  }, [attempt]);

  // Couldn't load: never silently hide the cancel controls from a paying owner.
  if (loadErr && !sub) {
    return (
      <div className="panel" style={{ marginBottom: 16 }} data-testid="subscription-card-error">
        <div className="panel-header"><h2>Subscription</h2></div>
        <p className="muted small" style={{ margin: 0 }}>
          We couldn't load your subscription details just now.{" "}
          <button type="button" className="btn-secondary" onClick={() => setAttempt((n) => n + 1)}>Try again</button>
        </p>
      </div>
    );
  }
  if (!sub || !sub.has_subscription) return null;

  async function manage(flow?: "cancel") {
    setOpening(true); setErr(null);
    try {
      const r = await billingSubscription.openPortal(flow);
      window.location.assign(r.url);
    } catch (e: any) {
      setErr(e?.message || "Couldn't open subscription settings. Try again in a minute.");
      setOpening(false);
    }
  }

  const renew = day(sub.current_period_end);
  const endsOn = day(sub.cancel_at) || renew;
  const statusText = sub.cancel_at_period_end
    ? `Cancellation scheduled — your subscription ends ${endsOn ?? "at the end of this billing period"} and won't renew`
    : (STATUS[sub.status || ""] || sub.status || "—");
  const warn = sub.cancel_at_period_end || sub.status === "past_due" || sub.status === "unpaid";

  return (
    <div className="panel" style={{ marginBottom: 16 }} data-testid="subscription-card">
      <div className="panel-header"><h2>Subscription</h2></div>
      <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap", justifyContent: "space-between" }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <div className="stat-icon"><CreditCard size={20} aria-hidden="true" /></div>
          <div>
            <strong style={{ fontSize: 15 }}>{sub.plan ? `${sub.plan.label} plan` : "Dispute Manager subscription"}</strong>
            {sub.plan && <span className="muted small"> · {sub.plan.max_clients == null ? "unlimited" : `up to ${sub.plan.max_clients}`} {sub.plan.max_clients === 1 ? "person" : "people"}</span>}
            <div className="small" style={{ color: warn ? "var(--ne-warning-text)" : "var(--ne-text-2)", marginTop: 2 }}>
              {statusText}
              {!sub.cancel_at_period_end && renew && sub.status === "active" && <span className="muted"> · renews {renew}</span>}
            </div>
          </div>
        </div>
        {sub.manageable ? (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button type="button" className="primary-button" onClick={() => manage()} disabled={opening} data-testid="manage-subscription">
              {opening ? "Opening…" : sub.status === "past_due" || sub.status === "unpaid" ? "Update payment method" : "Manage subscription"}
            </button>
            {!sub.cancel_at_period_end && sub.status !== "canceled" && (
              <button type="button" className="btn-secondary" onClick={() => manage("cancel")} disabled={opening} data-testid="cancel-subscription">
                Cancel subscription
              </button>
            )}
          </div>
        ) : (
          <span className="muted small">Only the account owner can change the subscription.</span>
        )}
      </div>
      <p className="muted small" style={{ margin: "12px 0 0" }}>
        Cancel anytime, update your card or download invoices on Stripe's secure page. A cancellation takes effect at the end of the month you've already paid for, and you won't be charged again.
      </p>
      {(sub.status === "past_due" || sub.status === "unpaid") && (
        <p className="small" role="alert" style={{ margin: "10px 0 0", color: "var(--ne-warning-text)" }}>
          Your last payment didn't go through. Update your card to keep your account active — we'll retry the charge automatically.
        </p>
      )}
      {returned && !err && <p className="small" role="status" style={{ margin: "8px 0 0", color: "var(--ne-success-text)" }}>Back from Stripe — changes can take a moment to show here.</p>}
      {renewed && !err && <p className="small" role="status" style={{ margin: "8px 0 0", color: "var(--ne-success-text)" }}>Welcome back — your subscription is active again.</p>}
      {err && <p className="error-text" role="alert" style={{ margin: "8px 0 0" }}>{err}</p>}
    </div>
  );
}
